<template>
  <b-form-group>
    <v-select
      v-model="selectedCustomer"
      :options="customerOptions"
      :filterable="false"
      placeholder="Select Customer"
      :loading="loadingCustomers || loadingSelectedCustomer"
      :clearable="true"
      @search="onSearch"
    >
      <template #no-options>
        <div class="text-center text-muted">No customers found</div>
      </template>

      <template #spinner="{ loading }">
        <div v-if="loading" class="spinner-border spinner-border-sm" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </template>

      <template #selected-option="{ id, code, name, ntnNumber }">
        <strong class="mr-1">{{ id }} | {{ name }} </strong>

        <b-badge :variant="!!ntnNumber.length > 0 ? 'success' : 'secondary'" class="ml-1">
          {{ ntnNumber.length > 0 ? 'Registered' : 'Unregistered' }}
        </b-badge>
      </template>

      <template #option="{ id, name, code, cellNumber, whatsappNumber, ntnNumber }">
        <b-row>
          <b-col md="12">
            <small class="text-muted d-block">{{ id }}</small>
          </b-col>
          <b-col md="12">
            <b-row>
              <b-col md="12">
                <strong class="mr-1">{{ name }}</strong>
                <span>{{ code }}</span>
              </b-col>
            </b-row>

            <b-row>
              <b-col md="6">
                <div>
                  <small class="text-muted font-weight-bold d-block">
                    {{ cellNumber }}
                  </small>
                </div>
                <div>
                  <small class="text-muted font-weight-bold d-block">
                    {{ whatsappNumber }}
                  </small>
                </div>
              </b-col>

              <b-col md="6" class="text-right">
                <b-badge :variant="ntnNumber.length > 0 ? 'success' : 'secondary'">
                  {{ ntnNumber.length > 0 ? 'Registered' : 'Unregistered' }}
                </b-badge>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </template>
    </v-select>
  </b-form-group>
</template>

<script>
import { BFormGroup } from 'bootstrap-vue';
import { api } from '@/utils/api';
import { BCol, BRow, BBadge } from 'bootstrap-vue';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import { ref, computed, watch, onMounted, onUnmounted } from '@vue/composition-api';
import store from '@/store';
import CustomerStoreModule from '../../customer/CustomerStoreModule';

export default {
  name: 'CustomerDropdownFilter',
  components: {
    vSelect,
    BRow,
    BCol,
    BFormGroup,
    BBadge,
  },
  props: {
    vid: {
      type: String,
    },
    name: {
      type: String,
      default: 'Customer',
    },
    value: {
      type: [Object, Number, String],
      default: () => null,
    },
    rules: {
      type: [Object, String],
      default: 'required',
    },
  },
  setup(props, { emit }) {
    const CUSTOMER_STORE_MODULE_NAME = 'customer-filter';

    // Register module
    if (!store.hasModule(CUSTOMER_STORE_MODULE_NAME))
      store.registerModule(CUSTOMER_STORE_MODULE_NAME, CustomerStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(CUSTOMER_STORE_MODULE_NAME))
        store.unregisterModule(CUSTOMER_STORE_MODULE_NAME);
    });

    // Reactive references
    const searchTerm = ref('');
    const pageNumber = ref(1);
    const loadingCustomers = ref(false);
    const loadingSelectedCustomer = ref(false);
    const customerOptions = ref([]);
    const selectedCustomer = ref(null);
    const searchTimeout = ref(null);

    // Computed properties
    const businessID = computed(() => store.state.user?.businessID);

    // Methods
    const fetchCustomers = async () => {
      loadingCustomers.value = true;
      try {
        const response = await api.get(`/CustomersFBR/GetByPagination/${businessID.value}`, {
          params: {
            searchTerm: searchTerm.value,
            pageNumber: pageNumber.value,
            pageSize: 20,
          },
        });

        if (
          response.data &&
          response.data.status === 'success' &&
          response.data.data &&
          response.data.data.items
        ) {
          customerOptions.value = [...response.data.data.items];
        } else {
          console.error(
            '=== CustomerDropdownFilter options Error in paginated customer data format:',
            response.data
          );
          customerOptions.value = [];
        }
      } catch (error) {
        console.error('=== CustomerDropdownFilter options Error fetching customers:', error);
        customerOptions.value = [];
      } finally {
        loadingCustomers.value = false;
      }
    };

    const onSearch = (searchTermValue) => {
      // Debounce search
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTerm.value = searchTermValue;

      if (!searchTermValue || searchTermValue.trim() === '') {
        searchTerm.value = '';
        fetchCustomers();
        return;
      }

      searchTimeout.value = setTimeout(() => {
        fetchCustomers();
      }, 350);
    };

    // Watchers
    watch(
      selectedCustomer,
      (newVal) => {
        emit('input', newVal?.id);
      },
      { immediate: true }
    );

    // Lifecycle hooks
    onMounted(() => {
      console.log('=== CustomerDropdownFilter mounted', props.value);

      const customerId = props.value;

      // Fetch initial data
      fetchCustomers();

      if (customerId) {
        loadingSelectedCustomer.value = true;
        store
          .dispatch('customer-filter/fetchCustomer', { id: customerId })
          .then((customer) => {
            selectedCustomer.value = customer;
          })
          .catch((error) => {
            console.log('error:', error);
          })
          .finally(() => {
            loadingSelectedCustomer.value = false;
          });
      }
    });

    onUnmounted(() => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
    });

    return {
      // Reactive references
      searchTerm,
      pageNumber,
      loadingCustomers,
      loadingSelectedCustomer,
      customerOptions,
      selectedCustomer,
      searchTimeout,

      // Computed properties
      businessID,

      // Methods
      fetchCustomers,
      onSearch,
    };
  },
};
</script>

<style lang="scss" scoped>
.v-select {
  &.is-invalid {
    .vs__dropdown-toggle {
      border-color: #dc3545;
    }
  }

  .vs__selected {
    width: 100%;
  }

  ::v-deep {
    .vs__dropdown-menu {
      max-height: 350px;
    }
  }
}
</style>
